/**
 * RecoveryScreen.jsx
 * Mobile App version of Recovery Tracking
 * Features: Sleep tracking, energy levels, stress, muscle soreness, recovery score
 */
import React, { useState, useEffect } from 'react';
import { 
  Moon, Sun, Battery, Brain, Activity, RefreshCw,
  ChevronRight, Plus, Zap, Heart, TrendingUp, TrendingDown,
  Check, X, Sparkles, Clock, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Muscle groups for soreness tracking
const MUSCLE_GROUPS = [
  { id: 'chest', name: 'Brust', icon: '💪', color: 'from-red-500/30 to-red-600/20' },
  { id: 'back', name: 'Rücken', icon: '🔙', color: 'from-blue-500/30 to-blue-600/20' },
  { id: 'shoulders', name: 'Schultern', icon: '🏋️', color: 'from-purple-500/30 to-purple-600/20' },
  { id: 'arms', name: 'Arme', icon: '💪', color: 'from-orange-500/30 to-orange-600/20' },
  { id: 'legs', name: 'Beine', icon: '🦵', color: 'from-green-500/30 to-green-600/20' },
  { id: 'core', name: 'Core', icon: '🎯', color: 'from-yellow-500/30 to-yellow-600/20' },
];

// Rating labels
const RATING_LABELS = {
  sleep_quality: ['Sehr schlecht', 'Schlecht', 'OK', 'Gut', 'Sehr gut'],
  energy_level: ['Erschöpft', 'Müde', 'Normal', 'Energiegeladen', 'Voller Power'],
  stress_level: ['Sehr hoch', 'Hoch', 'Normal', 'Niedrig', 'Sehr niedrig'],
};

// Soreness levels
const SORENESS_LEVELS = [
  { value: 0, label: 'Keine', color: 'bg-green-500' },
  { value: 1, label: 'Leicht', color: 'bg-yellow-500' },
  { value: 2, label: 'Mittel', color: 'bg-orange-500' },
  { value: 3, label: 'Stark', color: 'bg-red-500' },
];

export const RecoveryScreen = ({ portalToken, customerId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState([]);
  const [showEntryModal, setShowEntryModal] = useState(false);
  
  // Recovery entry form
  const [recoveryEntry, setRecoveryEntry] = useState({
    sleep_hours: 7,
    sleep_quality: 3,
    energy_level: 3,
    stress_level: 3,
    soreness: {},
    notes: ''
  });

  useEffect(() => {
    if (portalToken) {
      loadData();
    }
  }, [portalToken]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, historyRes] = await Promise.all([
        axios.get(`${API}/training-sync/dashboard`, {
          headers: { Authorization: `Bearer ${portalToken}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/training-sync/recovery`, {
          params: { days: 7 },
          headers: { Authorization: `Bearer ${portalToken}` }
        }).catch(() => ({ data: { entries: [] } }))
      ]);

      setDashboard(dashboardRes.data);
      setHistory(historyRes.data?.entries || []);
    } catch (error) {
      console.error('Failed to load recovery data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveRecovery = async () => {
    try {
      await axios.post(
        `${API}/training-sync/recovery`,
        {
          date: new Date().toISOString().split('T')[0],
          ...recoveryEntry
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success('Erholung erfasst! 🌙');
      setShowEntryModal(false);
      setRecoveryEntry({
        sleep_hours: 7,
        sleep_quality: 3,
        energy_level: 3,
        stress_level: 3,
        soreness: {},
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Calculate recovery score from entry
  const calculateRecoveryScore = (entry) => {
    const sleepScore = (entry.sleep_hours / 8) * 25;
    const qualityScore = (entry.sleep_quality / 5) * 25;
    const energyScore = (entry.energy_level / 5) * 25;
    const stressScore = ((6 - entry.stress_level) / 5) * 25; // Inverted
    
    return Math.min(100, Math.round(sleepScore + qualityScore + energyScore + stressScore));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-500';
    if (score >= 60) return 'from-yellow-500 to-amber-500';
    if (score >= 40) return 'from-orange-500 to-amber-500';
    return 'from-red-500 to-orange-500';
  };

  const recoveryScore = dashboard?.recovery_score || calculateRecoveryScore(recoveryEntry);
  const todayEntry = history.find(e => e.date === new Date().toISOString().split('T')[0]);

  return (
    <div className="p-4 space-y-4 pb-24">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/30 to-purple-500/20 flex items-center justify-center">
            <Moon className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">Recovery</h1>
            <p className="text-zinc-400 text-xs">Erholung & Regeneration</p>
          </div>
        </div>
        <button 
          onClick={loadData}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
          data-testid="refresh-recovery-btn"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-500 border-t-transparent mx-auto mb-3"></div>
          <p className="text-zinc-400 text-sm">Lade Recovery-Daten...</p>
        </div>
      ) : (
        <>
          {/* Recovery Score Card */}
          <div className="bg-zinc-800/50 rounded-2xl p-5" data-testid="recovery-score-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-zinc-400 text-sm">Recovery Score</p>
                <p className={`text-4xl font-bold ${getScoreColor(recoveryScore)}`}>
                  {recoveryScore}%
                </p>
              </div>
              <div className="w-20 h-20 relative">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18" cy="18" r="15.5"
                    fill="none"
                    stroke="#27272a"
                    strokeWidth="3"
                  />
                  <circle
                    cx="18" cy="18" r="15.5"
                    fill="none"
                    stroke="url(#scoreGradient)"
                    strokeWidth="3"
                    strokeDasharray={`${recoveryScore} 100`}
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={recoveryScore >= 60 ? '#22c55e' : '#ef4444'} />
                      <stop offset="100%" stopColor={recoveryScore >= 60 ? '#10b981' : '#f97316'} />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  {recoveryScore >= 70 ? (
                    <Zap className="w-6 h-6 text-green-400" />
                  ) : recoveryScore >= 50 ? (
                    <Battery className="w-6 h-6 text-yellow-400" />
                  ) : (
                    <Moon className="w-6 h-6 text-red-400" />
                  )}
                </div>
              </div>
            </div>
            
            <div className="text-sm">
              {recoveryScore >= 80 ? (
                <p className="text-green-400">🟢 Optimal erholt - Bereit für intensives Training!</p>
              ) : recoveryScore >= 60 ? (
                <p className="text-yellow-400">🟡 Gute Erholung - Normales Training möglich</p>
              ) : recoveryScore >= 40 ? (
                <p className="text-orange-400">🟠 Mässige Erholung - Leichteres Training empfohlen</p>
              ) : (
                <p className="text-red-400">🔴 Erhöhter Regenerationsbedarf - Ruhetag empfohlen</p>
              )}
            </div>
          </div>

          {/* Quick Log Button */}
          {!todayEntry && (
            <button
              onClick={() => setShowEntryModal(true)}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 flex items-center justify-between"
              data-testid="log-recovery-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Plus className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium">Erholung tracken</p>
                  <p className="text-white/70 text-xs">Schlaf, Energie & Stress erfassen</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/70" />
            </button>
          )}

          {/* Today's Stats (if entry exists) */}
          {todayEntry && (
            <div className="bg-zinc-800/50 rounded-2xl p-4" data-testid="today-entry">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white font-medium">Heutiger Eintrag</p>
                <span className="text-xs text-zinc-400">
                  {new Date().toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-900/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Moon className="w-4 h-4 text-indigo-400" />
                    <span className="text-zinc-400 text-xs">Schlaf</span>
                  </div>
                  <p className="text-white font-semibold">{todayEntry.sleep_hours}h</p>
                </div>
                
                <div className="bg-zinc-900/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Battery className="w-4 h-4 text-green-400" />
                    <span className="text-zinc-400 text-xs">Energie</span>
                  </div>
                  <p className="text-white font-semibold">{todayEntry.energy_level}/5</p>
                </div>
                
                <div className="bg-zinc-900/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="w-4 h-4 text-orange-400" />
                    <span className="text-zinc-400 text-xs">Stress</span>
                  </div>
                  <p className="text-white font-semibold">{todayEntry.stress_level}/5</p>
                </div>
                
                <div className="bg-zinc-900/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-purple-400" />
                    <span className="text-zinc-400 text-xs">Qualität</span>
                  </div>
                  <p className="text-white font-semibold">{todayEntry.sleep_quality}/5</p>
                </div>
              </div>
            </div>
          )}

          {/* Recovery History */}
          {history.length > 0 && (
            <div className="bg-zinc-800/50 rounded-2xl p-4" data-testid="recovery-history">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white font-medium">Letzte 7 Tage</p>
                <BarChart3 className="w-4 h-4 text-zinc-400" />
              </div>
              
              <div className="flex items-end gap-1 h-20">
                {history.slice(0, 7).reverse().map((entry, idx) => {
                  const score = calculateRecoveryScore(entry);
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <div 
                        className={`w-full rounded-t bg-gradient-to-t ${getScoreGradient(score)} transition-all`}
                        style={{ height: `${score}%` }}
                      />
                      <span className="text-[10px] text-zinc-500">
                        {new Date(entry.date).toLocaleDateString('de-DE', { weekday: 'short' }).slice(0, 2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tips Card */}
          <div className="bg-zinc-800/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <p className="text-white font-medium text-sm">Recovery-Tipps</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded-lg">
                <span className="text-lg">💤</span>
                <div>
                  <p className="text-white text-sm">7-9 Stunden Schlaf</p>
                  <p className="text-zinc-500 text-xs">Optimal für Muskelregeneration</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded-lg">
                <span className="text-lg">💧</span>
                <div>
                  <p className="text-white text-sm">Ausreichend trinken</p>
                  <p className="text-zinc-500 text-xs">2-3 Liter Wasser pro Tag</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded-lg">
                <span className="text-lg">🧘</span>
                <div>
                  <p className="text-white text-sm">Aktive Erholung</p>
                  <p className="text-zinc-500 text-xs">Leichte Bewegung an Ruhetagen</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Entry Modal */}
      {showEntryModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center">
          <div className="bg-zinc-900 rounded-t-3xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">Erholung erfassen</h2>
              <button onClick={() => setShowEntryModal(false)} className="p-2">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="space-y-5">
              {/* Sleep Hours */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-zinc-400 text-sm flex items-center gap-2">
                    <Moon className="w-4 h-4" />
                    Schlafstunden
                  </label>
                  <span className="text-white font-bold">{recoveryEntry.sleep_hours}h</span>
                </div>
                <input
                  type="range"
                  min="3"
                  max="12"
                  step="0.5"
                  value={recoveryEntry.sleep_hours}
                  onChange={(e) => setRecoveryEntry(r => ({ ...r, sleep_hours: parseFloat(e.target.value) }))}
                  className="w-full accent-indigo-500"
                />
                <div className="flex justify-between text-xs text-zinc-500 mt-1">
                  <span>3h</span>
                  <span>12h</span>
                </div>
              </div>

              {/* Sleep Quality */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Schlafqualität
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[1, 2, 3, 4, 5].map(level => (
                    <button
                      key={level}
                      onClick={() => setRecoveryEntry(r => ({ ...r, sleep_quality: level }))}
                      className={`py-3 rounded-lg text-sm font-medium transition-all ${
                        recoveryEntry.sleep_quality === level 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <p className="text-center text-xs text-zinc-500 mt-1">
                  {RATING_LABELS.sleep_quality[recoveryEntry.sleep_quality - 1]}
                </p>
              </div>

              {/* Energy Level */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block flex items-center gap-2">
                  <Battery className="w-4 h-4" />
                  Energie-Level
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[1, 2, 3, 4, 5].map(level => (
                    <button
                      key={level}
                      onClick={() => setRecoveryEntry(r => ({ ...r, energy_level: level }))}
                      className={`py-3 rounded-lg text-sm font-medium transition-all ${
                        recoveryEntry.energy_level === level 
                          ? 'bg-green-600 text-white' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <p className="text-center text-xs text-zinc-500 mt-1">
                  {RATING_LABELS.energy_level[recoveryEntry.energy_level - 1]}
                </p>
              </div>

              {/* Stress Level */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  Stress-Level
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[1, 2, 3, 4, 5].map(level => (
                    <button
                      key={level}
                      onClick={() => setRecoveryEntry(r => ({ ...r, stress_level: level }))}
                      className={`py-3 rounded-lg text-sm font-medium transition-all ${
                        recoveryEntry.stress_level === level 
                          ? 'bg-orange-600 text-white' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <p className="text-center text-xs text-zinc-500 mt-1">
                  {RATING_LABELS.stress_level[recoveryEntry.stress_level - 1]}
                </p>
              </div>

              {/* Muscle Soreness */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Muskelkater
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {MUSCLE_GROUPS.map(muscle => {
                    const soreness = recoveryEntry.soreness[muscle.id] || 0;
                    const sorenessInfo = SORENESS_LEVELS[soreness];
                    return (
                      <button
                        key={muscle.id}
                        onClick={() => {
                          const newSoreness = (soreness + 1) % 4;
                          setRecoveryEntry(r => ({
                            ...r,
                            soreness: { ...r.soreness, [muscle.id]: newSoreness }
                          }));
                        }}
                        className={`p-3 rounded-lg text-left transition-all ${
                          soreness > 0 ? 'bg-zinc-700' : 'bg-zinc-800'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-lg">{muscle.icon}</span>
                          <div className={`w-2 h-2 rounded-full ${sorenessInfo.color}`} />
                        </div>
                        <p className="text-white text-xs font-medium">{muscle.name}</p>
                        <p className="text-zinc-500 text-[10px]">{sorenessInfo.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Notizen (optional)</label>
                <textarea
                  value={recoveryEntry.notes}
                  onChange={(e) => setRecoveryEntry(r => ({ ...r, notes: e.target.value }))}
                  placeholder="Wie fühlst du dich heute?"
                  className="w-full bg-zinc-800 rounded-lg p-3 text-white text-sm resize-none h-20"
                />
              </div>

              {/* Preview Score */}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Voraussichtlicher Score</span>
                  <span className={`text-2xl font-bold ${getScoreColor(calculateRecoveryScore(recoveryEntry))}`}>
                    {calculateRecoveryScore(recoveryEntry)}%
                  </span>
                </div>
              </div>
            </div>
            
            <button
              onClick={saveRecovery}
              className="w-full mt-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              data-testid="save-recovery-btn"
            >
              <Check className="w-4 h-4" />
              Erholung speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecoveryScreen;
